import { CartLinesUpdateEvent } from '@shopify/events';
(() => {
  const ELEMENT_NAME = 'pf-quantity-bundle';
  const INITIAL_BIND_ATTEMPTS = 20;
  const INITIAL_BIND_DELAY = 100;
  const INITIAL_OBSERVER_TIMEOUT = 2500;

  if (customElements.get(ELEMENT_NAME)) return;

  class PetifoQuantityBundle extends HTMLElement {
    constructor() {
      super();

      this.form = null;
      this.formSubmitButton = null;
      this.variants = new Map();
      this.baseVariantId = '';
      this.eventRoot = null;
      this.syncFrame = null;
      this.initialRetryTimer = null;
      this.initialObserverTimer = null;
      this.initialObserver = null;
      this.statusTimer = null;
      this.busy = false;
      this.controlDisabledState = new WeakMap();

      this.onPageShow = this.onPageShow.bind(this);
      this.onTierChange = this.onTierChange.bind(this);
      this.onUnitVariantChange = this.onUnitVariantChange.bind(this);
      this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onProductChange = this.onProductChange.bind(this);
      this.onVariantEvent = this.onVariantEvent.bind(this);
    }

    connectedCallback() {
      this.parseVariants();
      this.tierInputs.forEach((input) => input.addEventListener('change', this.onTierChange));
      this.unitSelects.forEach((select) => select.addEventListener('change', this.onUnitVariantChange));

      this.eventRoot = this.sectionRoot;
      this.eventRoot?.addEventListener('change', this.onProductChange, true);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('variant:update', this.onVariantEvent);
      document.addEventListener('variant:selected', this.onVariantEvent);
      document.addEventListener('variant:change', this.onVariantEvent);

      this.startInitialFormBinding();
      this.updateSelection();
      this.updateUnitImages();
    }

    disconnectedCallback() {
      this.tierInputs.forEach((input) => input.removeEventListener('change', this.onTierChange));
      this.unitSelects.forEach((select) => select.removeEventListener('change', this.onUnitVariantChange));

      this.eventRoot?.removeEventListener('change', this.onProductChange, true);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('variant:update', this.onVariantEvent);
      document.removeEventListener('variant:selected', this.onVariantEvent);
      document.removeEventListener('variant:change', this.onVariantEvent);

      this.stopInitialFormBinding();
      this.unbindProductForm();
      window.cancelAnimationFrame(this.syncFrame);
      window.clearTimeout(this.statusTimer);
    }

    get sectionRoot() {
      return this.closest('.shopify-section') || this.parentElement?.closest('[data-section-id]') || document;
    }

    get tierInputs() {
      return [...this.querySelectorAll('[data-pf-qb-tier] input[type="radio"]')];
    }

    get tierElements() {
      return [...this.querySelectorAll('[data-pf-qb-tier]')];
    }

    get unitSelects() {
      return [...this.querySelectorAll('[data-pf-qb-variant-select]')];
    }

    get selectedTier() {
      return this.querySelector('[data-pf-qb-tier] input[type="radio"]:checked')?.closest('[data-pf-qb-tier]') || null;
    }

    parseVariants() {
      const source = this.querySelector('[data-pf-qb-variants]');
      if (!source) return;

      try {
        const variants = JSON.parse(source.textContent || '[]');
        variants.forEach((variant) => {
          const price = Number(variant.price) || 0;
          const compareAt = Number(variant.compare_at_price) || 0;
          this.variants.set(String(variant.id), {
            ...variant,
            id: String(variant.id),
            price,
            regularPrice: compareAt > price ? compareAt : price,
            image: String(variant.image || ''),
          });
        });
      } catch (error) {
        console.error('[PetifoQuantityBundle] Invalid variant data.', error);
      }
    }

    startInitialFormBinding() {
      this.stopInitialFormBinding();
      let attempts = 0;

      const attemptBinding = () => {
        if (!this.isConnected) return;
        attempts += 1;

        if (this.bindProductForm()) {
          this.syncFromProductForm(true);
          this.setProductFormQuantity(this.currentQuantity());
          this.stopInitialFormBinding();
          return;
        }

        if (attempts < INITIAL_BIND_ATTEMPTS) {
          this.initialRetryTimer = window.setTimeout(attemptBinding, INITIAL_BIND_DELAY);
        }
      };

      const observedRoot = this.eventRoot || this.sectionRoot;
      if (observedRoot instanceof Node) {
        this.initialObserver = new MutationObserver(() => {
          if (!this.isConnected || this.form?.isConnected) return;
          if (this.bindProductForm()) {
            this.syncFromProductForm(true);
            this.setProductFormQuantity(this.currentQuantity());
            this.stopInitialFormBinding();
          }
        });
        this.initialObserver.observe(observedRoot, { childList: true, subtree: true });
        this.initialObserverTimer = window.setTimeout(
          () => this.stopInitialFormBinding(),
          INITIAL_OBSERVER_TIMEOUT
        );
      }

      attemptBinding();
    }

    stopInitialFormBinding() {
      window.clearTimeout(this.initialRetryTimer);
      window.clearTimeout(this.initialObserverTimer);
      this.initialRetryTimer = null;
      this.initialObserverTimer = null;
      this.initialObserver?.disconnect();
      this.initialObserver = null;
    }

    bindProductForm() {
      const scope = this.eventRoot || this.sectionRoot || document;
      const sectionId = String(this.dataset.sectionId || '');
      const exactForm = sectionId
        ? document.getElementById(`BuyButtons-ProductForm-${sectionId}`)
        : null;
      const forms = [...scope.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')];
      const nextForm =
        (exactForm instanceof HTMLFormElement && exactForm.querySelector('input[name="id"], select[name="id"]')
          ? exactForm
          : null) ||
        forms.find(
          (form) => !form.closest('quick-add-component') && form.querySelector('input[name="id"], select[name="id"]')
        );

      if (!nextForm) {
        if (this.form && !this.form.isConnected) this.unbindProductForm();
        return false;
      }
      if (nextForm === this.form) return true;

      this.unbindProductForm();
      this.form = nextForm;
      this.form.addEventListener('submit', this.onFormSubmit, true);
      this.formSubmitButton =
        this.form.querySelector('button[type="submit"][name="add"]') ||
        this.form.querySelector('button[type="submit"]') ||
        null;
      this.formSubmitButton?.addEventListener('click', this.onSubmitButtonClick, true);
      return true;
    }

    unbindProductForm() {
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      this.formSubmitButton?.removeEventListener('click', this.onSubmitButtonClick, true);
      this.form = null;
      this.formSubmitButton = null;
    }

    onPageShow() {
      this.bindProductForm();
      this.updateSelection();
      this.updateUnitImages();
    }

    onTierChange(event) {
      if (!event.target.checked) return;
      this.updateSelection();
    }

    onUnitVariantChange(event) {
      this.updateUnitImage(event.currentTarget);
      this.updatePrices();
    }

    onProductChange(event) {
      const target = event.target;
      if (!(target instanceof Element) || this.contains(target)) return;

      if (
        target.matches('input[name="id"], select[name="id"]') ||
        target.closest('variant-picker, [data-variant-picker], product-form-component')
      ) {
        this.scheduleProductSync();
      }
    }

    onVariantEvent(event) {
      const eventSection = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
      const ownSection = this.closest('.shopify-section');
      if (eventSection && ownSection && eventSection !== ownSection) return;
      this.scheduleProductSync();
    }

    scheduleProductSync() {
      window.cancelAnimationFrame(this.syncFrame);
      this.syncFrame = window.requestAnimationFrame(() => {
        this.bindProductForm();
        this.syncFromProductForm(false);
        this.setProductFormQuantity(this.currentQuantity());
      });
    }

    syncFromProductForm(force) {
      if (!this.form) return;
      const nextVariantId = String(this.form.querySelector('input[name="id"], select[name="id"]')?.value || '');
      if (!nextVariantId || (!force && nextVariantId === this.baseVariantId)) return;
      if (!this.variants.has(nextVariantId)) return;

      this.baseVariantId = nextVariantId;
      this.unitSelects.forEach((select) => {
        if ([...select.options].some((option) => option.value === nextVariantId && !option.disabled)) {
          select.value = nextVariantId;
        }
      });
      this.updateUnitImages();
      this.updatePrices();
    }

    currentQuantity() {
      return Math.max(1, Number(this.selectedTier?.dataset.quantity) || 1);
    }

    updateSelection() {
      const tier = this.selectedTier;
      if (!tier) return;

      const quantity = this.currentQuantity();
      this.tierElements.forEach((element) => element.classList.toggle('is-selected', element === tier));
      this.setProductFormQuantity(quantity);

      const variantsPanel = this.querySelector('[data-pf-qb-variants-panel]');
      if (variantsPanel) {
        // The panel keeps one place under the rows, so choosing a row never moves the rows or the page (Peji 2026-11-03).
        variantsPanel.hidden = quantity <= 1;
      }
      this.querySelectorAll('[data-pf-qb-unit]').forEach((row) => {
        row.hidden = Number(row.dataset.pfQbUnit) > quantity;
      });

      this.form?.querySelectorAll('shopify-accelerated-checkout, .shopify-payment-button').forEach(el => { el.hidden = quantity > 1; });
      this.updateUnitImages();
      this.updatePrices();
    }

    setProductFormQuantity(quantity) {
      if (!this.form) return;

      let quantityInput = this.form.querySelector('input[name="quantity"]');
      if (!quantityInput) {
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        quantityInput.dataset.pfQbQuantity = 'true';
        this.form.appendChild(quantityInput);
      }

      const nextValue = String(quantity);
      if (quantityInput.value === nextValue) return;
      quantityInput.value = nextValue;
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateUnitImages() {
      this.unitSelects.forEach((select) => this.updateUnitImage(select));
    }

    updateUnitImage(select) {
      const row = select?.closest('[data-pf-qb-unit]');
      const imageContainer = row?.querySelector('.pf-qb__variant-image');
      if (!imageContainer) return;

      const variant = this.variants.get(String(select.value));
      const imageUrl = variant?.image || select.selectedOptions?.[0]?.dataset.image || '';
      let image = imageContainer.querySelector('[data-pf-qb-unit-image]');

      if (!imageUrl) {
        image?.remove();
        return;
      }

      if (!image) {
        image = document.createElement('img');
        image.alt = '';
        image.width = 40;
        image.height = 40;
        image.loading = 'lazy';
        image.dataset.pfQbUnitImage = '';
        imageContainer.appendChild(image);
      }
      if (image.src !== imageUrl) image.src = imageUrl;
    }

    updatePrices() {
      const baseVariant = this.variants.get(this.baseVariantId) || this.variants.values().next().value;
      if (!baseVariant) return;

      this.tierElements.forEach((tier) => {
        const quantity = Math.max(1, Number(tier.dataset.quantity) || 1);
        const discount = Math.max(0, Math.min(100, Number(tier.dataset.discount) || 0));
        const tierVariants = this.variantsForQuantity(quantity, baseVariant);
        const saleSubtotal = tierVariants.reduce((sum, variant) => sum + variant.price, 0);
        const regularSubtotal = tierVariants.reduce((sum, variant) => sum + variant.regularPrice, 0);
        const variantTotals = new Map();
        tierVariants.forEach(v => variantTotals.set(v.id, (variantTotals.get(v.id) || 0) + v.price));
        const total = [...variantTotals.values()].reduce((sum, cents) => sum + cents - Math.floor(cents * discount / 100), 0);
        // Keep visible savings equal to the two displayed whole prices.
        const savings = Math.max(0, Math.ceil(regularSubtotal / 100) - Math.ceil(total / 100)) * 100;

        const compareElement = tier.querySelector('[data-pf-qb-compare]');
        const totalElement = tier.querySelector('[data-pf-qb-total]');
        const badgeElement = tier.querySelector('[data-pf-qb-badge]');

        if (compareElement) {
          compareElement.textContent = this.formatMoney(regularSubtotal);
          compareElement.hidden = regularSubtotal <= total;
        }
        if (totalElement) totalElement.textContent = this.formatMoney(total);

        if (badgeElement) {
          const template = badgeElement.dataset.template || '';
          badgeElement.textContent = template.replace(/\[amount\]/gi, this.formatMoney(savings));
          badgeElement.hidden = !template.trim() || savings <= 0;
        }
      });
    }

    variantsForQuantity(quantity, fallbackVariant) {
      if (quantity === 1) return [fallbackVariant];

      if (this.unitSelects.length === 0) {
        return Array.from({ length: quantity }, () => fallbackVariant);
      }

      return Array.from({ length: quantity }, (_, index) => {
        const selectedId = this.unitSelects[index]?.value;
        return this.variants.get(String(selectedId)) || fallbackVariant;
      });
    }

    formatMoney(cents) {
      const whole = Math.ceil(cents / 100);
      const zero = this.dataset.moneyZero || '';
      const sample = this.dataset.moneySample || '';
      const number = /[0-9][0-9.,\u00A0\u202F ']*[0-9]|[0-9]/;
      const zeroMatch = zero.match(number);
      const sampleMatch = sample.match(number);
      if (zeroMatch && sampleMatch) {
        const group = sampleMatch[0].match(/^1(\D)111/);
        const digits = group ? String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, group[1]) : String(whole);
        return zero.slice(0, zeroMatch.index) + digits + zero.slice(zeroMatch.index + zeroMatch[0].length);
      }
      const currency = this.dataset.currencyCode || window.Shopify?.currency?.active || 'EUR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency, currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0, maximumFractionDigits: 0,
        }).format(whole);
      } catch {
        return `${whole} ${currency}`;
      }
    }

    onSubmitButtonClick(event) {
      if (this.currentQuantity() <= 1) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void this.submitBundle();
    }

    onFormSubmit(event) {
      // The size guide explicitly buys the recommended variant, not this multi-unit bundle.
      if (this.form?.pfSizeGuideRequest) return;
      if (this.busy || !this.form) {
        if (this.busy) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        return;
      }

      const quantity = this.currentQuantity();
      if (quantity <= 1) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void this.submitBundle();
    }

    async submitBundle() {
      if (this.busy || !this.form) return;
      if (!this.form.reportValidity()) return;

      const quantity = this.currentQuantity();
      const baseVariant = this.variants.get(this.baseVariantId) || this.variants.values().next().value;
      const selectedVariants = this.variantsForQuantity(quantity, baseVariant);

      if (selectedVariants.length !== quantity || selectedVariants.some((variant) => !variant?.available)) {
        this.setStatus(
          this.dataset.errorMessage || '',
          'error'
        );
        return;
      }

      this.setProductFormQuantity(quantity);
      await this.addBundle(selectedVariants, quantity);
    }

    async addBundle(selectedVariants, quantity) {
      this.setBusy(true);
      const pending = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: selectedVariants.map(variant => ({ merchandiseId: String(variant.id), quantity: 1 })),
        promise: pending.promise,
      }));

      try {
        const nativeFormData = new FormData(this.form);
        const sellingPlan = nativeFormData.get('selling_plan');
        const sharedProperties = this.readProductProperties(nativeFormData);
        const groupedVariants = new Map();

        selectedVariants.forEach((variant) => {
          const id = String(variant.id);
          groupedVariants.set(id, (groupedVariants.get(id) || 0) + 1);
        });

        const requestBody = new FormData();
        [...groupedVariants.entries()].forEach(([variantId, itemQuantity], itemIndex) => {
          requestBody.append(`items[${itemIndex}][id]`, variantId);
          requestBody.append(`items[${itemIndex}][quantity]`, String(itemQuantity));
          if (sellingPlan) requestBody.append(`items[${itemIndex}][selling_plan]`, String(sellingPlan));

          sharedProperties.forEach(({ name, value }) => {
            requestBody.append(`items[${itemIndex}][properties][${name}]`, value);
          });
        });

        const cartSectionIds = this.getCartSectionIds();
        if (cartSectionIds.length) requestBody.append('sections', cartSectionIds.join(','));
        requestBody.append('sections_url', window.location.pathname);

        const root = window.Shopify?.routes?.root || '/';
        const endpoint = `${root.replace(/\/?$/, '/')}cart/add.js`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: requestBody,
        });
        const responseData = await response.json().catch(() => ({}));

        if (!response.ok || responseData.status || responseData.errors) {
          throw new Error(
            responseData.description ||
              responseData.message ||
              responseData.errors ||
              this.dataset.errorMessage ||
              ''
          );
        }

        const ajaxCart = await fetch(`${root.replace(/\/?$/, '/')}cart.js`, { headers: { Accept: 'application/json' } }).then(response => {
          if (!response.ok) throw new Error(this.dataset.errorMessage);
          return response.json();
        });
        pending.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items, source: 'product-form-component', itemCount: quantity,
            productId: this.dataset.productId, sections: responseData.sections || {}, didError: false,
          },
        });

        this.setStatus(this.dataset.successMessage || '', 'success');
      } catch (error) {
        pending.reject(error);
        console.error('[PetifoQuantityBundle] Add failed.', error);
        this.setStatus(
          error?.message || this.dataset.errorMessage || '',
          'error'
        );
      } finally {
        this.setBusy(false);
      }
    }

    readProductProperties(formData) {
      const properties = [];

      for (const [fieldName, value] of formData.entries()) {
        const match = fieldName.match(/^properties\[(.*)\]$/);
        if (!match) continue;
        if (value instanceof File && value.size === 0) continue;
        properties.push({ name: match[1], value });
      }

      return properties;
    }

    getCartSectionIds() {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);

      if (!sectionIds.length) {
        const drawer = document.querySelector('cart-drawer-component');
        const fallbackId =
          drawer?.dataset.sectionId ||
          drawer?.closest('[id^="shopify-section-"]')?.id.replace(/^shopify-section-/, '');
        if (fallbackId) sectionIds.push(fallbackId);
      }

      return [...new Set(sectionIds)];
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.toggleAttribute('aria-busy', isBusy);
      this.querySelectorAll('input, select').forEach((control) => {
        if (isBusy) {
          this.controlDisabledState.set(control, control.disabled);
          control.disabled = true;
        } else if (this.controlDisabledState.has(control)) {
          control.disabled = this.controlDisabledState.get(control);
          this.controlDisabledState.delete(control);
        }
      });

      if (this.formSubmitButton) {
        if (isBusy) {
          this.formSubmitButton.dataset.pfQbWasDisabled = this.formSubmitButton.disabled ? 'true' : 'false';
          this.formSubmitButton.disabled = true;
          this.formSubmitButton.setAttribute('aria-disabled', 'true');
        } else {
          const wasDisabled = this.formSubmitButton.dataset.pfQbWasDisabled === 'true';
          this.formSubmitButton.disabled = wasDisabled;
          this.formSubmitButton.toggleAttribute('aria-disabled', wasDisabled);
          delete this.formSubmitButton.dataset.pfQbWasDisabled;
        }
      }
    }

    setStatus(message, state = 'info') {
      const status = this.querySelector('[data-pf-qb-status]');
      if (!status) return;

      window.clearTimeout(this.statusTimer);
      status.textContent = String(message || '');
      status.dataset.state = state;

      if (message && state === 'success') {
        this.statusTimer = window.setTimeout(() => {
          status.textContent = '';
          delete status.dataset.state;
        }, 4000);
      }
    }
  }

  customElements.define(ELEMENT_NAME, PetifoQuantityBundle);
})();
